import jsep, {
  Expression as JsepExpression,
  MemberExpression,
  BinaryExpression,
  UnaryExpression,
  Identifier,
  Literal,
  Compound,
  CallExpression,
} from 'jsep';

export enum Comparison {
  Equal = 'equal',
  NotEqual = 'not-equal',
  GreaterThan = 'greater-than',
  LessThan = 'less-than',
  Filled = 'filled',
  NotFilled = 'not-filled',
  Includes = 'includes',
}

type SingleCondition = {
  field: string;
  operator: Comparison;
  value?: string;
  logicalOperator?: '&&' | '||';
};

const operatorToComparison: Record<string, Comparison> = {
  '!': Comparison.NotFilled,
  '!!': Comparison.Filled,
  '!==': Comparison.NotEqual,
  '<': Comparison.LessThan,
  '===': Comparison.Equal,
  '>': Comparison.GreaterThan,
};

const comparisonToOperator: Record<Comparison, string> = {
  [Comparison.Equal]: '===',
  [Comparison.NotEqual]: '!==',
  [Comparison.LessThan]: '<',
  [Comparison.GreaterThan]: '>',
  [Comparison.Filled]: '!!',
  [Comparison.NotFilled]: '!',
  [Comparison.Includes]: '.includes()',
};

export const ConditionsLabel: Record<Comparison, string> = {
  [Comparison.Equal]: 'Equal',
  [Comparison.NotEqual]: 'Not equal',
  [Comparison.GreaterThan]: 'Greater than',
  [Comparison.LessThan]: 'Less than',
  [Comparison.Filled]: 'Filled',
  [Comparison.NotFilled]: 'Not filled',
  [Comparison.Includes]: 'Includes',
};

const addCondition = (
  conditions: SingleCondition[],
  field: string,
  operator: Comparison,
  value?: string,
  logicalOperator?: '&&' | '||',
): void => {
  conditions.push({ field, logicalOperator, operator, value });
};

const serializeField = (node: JsepExpression): string => {
  switch (node.type) {
  case 'Identifier':
    return (node as Identifier).name;
  case 'Literal':
    return typeof (node as Literal).value === 'string' ? `"${(node as Literal).value}"` : String((node as Literal).value);
  case 'CallExpression': {
    const callNode = node as CallExpression;
    const callee = serializeField(callNode.callee);
    const args = callNode.arguments.map(arg => serializeField(arg)).join(', ');
    return `${callee}(${args})`;
  }
  case 'MemberExpression': {
    const memberNode = node as MemberExpression;
    const object = serializeField(memberNode.object);
    const property = serializeField(memberNode.property);
    if (memberNode.computed) {
      return `${object}[${property}]`;
    }
    return `${object}.${property}`;

  }
  default:
    console.error(`Unsupported field node type: ${node.type}`);
    return '';
  }
};

const getFullOperatorAndField = (unaryNode: UnaryExpression): { operator: string; field: string | null } => {
  let operator = '';
  let currentNode: JsepExpression = unaryNode;

  while (currentNode.type === 'UnaryExpression') {
    operator += (currentNode as UnaryExpression).operator;
    currentNode = (currentNode as UnaryExpression).argument;
  }

  if (currentNode.type === 'Identifier' || currentNode.type === 'MemberExpression') {
    const field = serializeField(currentNode);
    return { field, operator };
  }

  console.error(`Unsupported argument type: ${currentNode.type}`);
  return { field: null, operator };
};

const parseConditions = (expression: string): SingleCondition[] => {
  const conditions: SingleCondition[] = [];

  // match and replace object pattern
  const placeholder = '__SKIPPEDNODE__';
  const replacedExpression = expression.replace(/\{[^{}]*\}/g, placeholder);

  try {
    const ast = jsep(replacedExpression);

    const extractConditions = (node: JsepExpression, currentLogicalOperator?: '&&' | '||'): void => {
      if (node.type === 'BinaryExpression') {
        const binaryNode = node as BinaryExpression;

        if (binaryNode.operator === '&&' || binaryNode.operator === '||') {
          extractConditions(binaryNode.left, currentLogicalOperator);
          extractConditions(binaryNode.right, binaryNode.operator as '&&' | '||');
        } else {
          const { operator, left, right } = binaryNode;
          const comparisonOperator = operatorToComparison[operator];

          if (!comparisonOperator) {
            console.error(`Unsupported operator: ${operator}`);
            return;
          }

          const field = serializeField(left);

          if (right.type !== 'Literal') {
            // Ignore objects
            if (right.type === 'Identifier' && (right as Identifier).name === placeholder) {
              return;
            }
            console.error(`Unsupported or skipped right operand type: ${right.type}`);
            return;
          }

          addCondition(conditions, field, comparisonOperator, String((right as Literal).value), currentLogicalOperator);
        }
      } else if (node.type === 'UnaryExpression') {
        const { operator, field } = getFullOperatorAndField(node as UnaryExpression);

        if (!field) {
          return;
        }

        const comparisonOperator = operatorToComparison[operator];
        if (!comparisonOperator) {
          console.error(`Unsupported operator: ${operator}`);
          return;
        }

        addCondition(conditions, field, comparisonOperator, undefined, currentLogicalOperator);
      } else if (node.type === 'CallExpression') {
        const callNode = node as CallExpression;
        if (
          callNode.callee.type === 'MemberExpression'
          && (callNode.callee.property as Identifier).name === 'includes'
        ) {
          const field = serializeField(callNode);
          if (callNode.arguments.length !== 1 || callNode.arguments[0].type !== 'Literal') {
            console.error('Unsupported .includes() argument format');
            return;
          }
          const value = String(callNode.arguments[0].value);
          addCondition(conditions, field, Comparison.Includes, value, currentLogicalOperator);
        }
      } else {
        console.error(`Unsupported node type: ${node.type}`);
      }
    };

    extractConditions(ast);
  } catch (error) {
    console.error('Failed to parse expression:', error);
    throw error;
  }

  if (conditions.length > 0) {
    conditions[0].logicalOperator = undefined;
  }

  return conditions;
};

const supportedOperators = ['&&', '||', ...Object.keys(operatorToComparison)];

const isSimpleExpression = (expression: string): boolean => {
  if (!expression) return true;
  try {
    const ast = jsep(expression);

    let isValidOperand: (node: JsepExpression) => boolean;

    const isSupportedNode = (node: JsepExpression): boolean => {

      if (node.type === 'BinaryExpression') {
        const binaryNode = node as BinaryExpression;

        if (supportedOperators.includes(binaryNode.operator)) {
          if (binaryNode.operator === '&&' || binaryNode.operator === '||') {
            return (
              isSupportedNode(binaryNode.left) && isSupportedNode(binaryNode.right)
            );
          }
          return (
            isValidOperand(binaryNode.left) && isValidOperand(binaryNode.right)
          );
        }
        return false;
      }

      if (node.type === 'UnaryExpression') {
        const unaryNode = node as UnaryExpression;
        if (operatorToComparison[unaryNode.operator]) {
          return isValidOperand(unaryNode.argument);
        }
        return false;
      }

      return false;
    };

    isValidOperand = (node: JsepExpression): boolean => {
      if (
        node.type === 'Identifier'
        || node.type === 'Literal'
        || node.type === 'MemberExpression'
      ) {
        return true;
      }
      if (node.type === 'UnaryExpression' || node.type === 'BinaryExpression') {
        return isSupportedNode(node);
      }
      return false;
    };

    return isSupportedNode(ast);
  } catch (error) {
    return false;
  }
};

const buildExpressionAst = (conditions: SingleCondition[]): JsepExpression | null => {
  if (conditions.length === 0) return null;

  const conditionNodes = conditions.map(cond => {
    const fieldNode = jsep(cond.field);

    const operator = comparisonToOperator[cond.operator];

    if (operator === '!' || operator === '!!') {
      return {
        argument: fieldNode,
        operator,
        prefix: true,
        type: 'UnaryExpression',
      } as UnaryExpression;
    }

    if (operator === '.includes()') {
      return {
        arguments: [
          {
            raw: JSON.stringify(cond.value),
            type: 'Literal',
            value: cond.value,
          },
        ],
        callee: {
          computed: false,
          object: fieldNode,
          property: { name: 'includes', type: 'Identifier' },
          type: 'MemberExpression',
        },
        type: 'CallExpression',
      };
    }

    const valueNode: Literal = {
      raw: cond.value ? String(cond.value) : '',
      type: 'Literal',
      value: cond.value !== undefined && !Number.isNaN(Number(cond.value)) ? Number(cond.value) : cond.value ?? null,
    };
    return {
      left: fieldNode,
      operator,
      right: valueNode,
      type: 'BinaryExpression',
    } as BinaryExpression;
  });

  return conditionNodes.reduce((accumulator, currentNode, index) => {
    if (index === 0) return currentNode;
    return {
      left: accumulator,
      operator: conditions[index].logicalOperator || '&&',
      right: currentNode,
      type: 'BinaryExpression',
    } as BinaryExpression;
  });
};

const generateExpressionString = (ast: JsepExpression): string => {
  const serializeNode = (node: JsepExpression): string => {
    switch (node.type) {
    case 'Compound':
      return (node as Compound).body.map(serializeNode).join(', ');
    case 'BinaryExpression':
      return `${serializeNode((node as BinaryExpression).left)} ${(node as BinaryExpression).operator} ${serializeNode(
        (node as BinaryExpression).right,
      )}`;
    case 'UnaryExpression':
      return `${(node as UnaryExpression).operator}${serializeNode((node as UnaryExpression).argument)}`;
    case 'Identifier':
      return (node as Identifier).name;
    case 'Literal': {
      const { value } = node as Literal;
      return typeof value === 'string' ? `"${value}"` : String(value);
    }
    case 'MemberExpression': {
      const memberNode = node as MemberExpression;
      const object = serializeNode(memberNode.object);
      const property = serializeNode(memberNode.property);
      if (memberNode.computed) {
        return `${object}[${property}]`;
      }
      return `${object}.${property}`;
    }
    case 'CallExpression': {
      const callNode = node as CallExpression;
      const callee = serializeNode(callNode.callee); // Serialize the function being called
      const args = callNode.arguments.map(arg => serializeNode(arg)).join(', ');
      if (callNode.callee.type === 'MemberExpression') {
        const memberNode = callNode.callee as MemberExpression;
        const object = serializeNode(memberNode.object);
        const method = serializeNode(memberNode.property);

        if (method === 'includes') {
          return `${object}.includes(${args})`;
        }
      }

      return `${callee}(${args})`;
    }
    default:
      throw new Error(`Unsupported node type: ${node.type}`);
    }
  };

  return serializeNode(ast);
};

export { parseConditions, buildExpressionAst, generateExpressionString, isSimpleExpression };
